/* eslint-disable @angular-eslint/component-selector */
import { UserComponent } from 'app/layout/common/user/user.component';
import { AuthService } from 'app/modules/auth/services';
import { Navigation } from 'app/shared/models/ui/navigation.interface';
import { NavigationService } from 'app/shared/services';
import { isEzvoltPartner } from 'app/shared/utils';
import { filter, Subject, takeUntil } from 'rxjs';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseNavigationItem,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'modern-layout',
  templateUrl: './modern.component.html',
  styleUrls: ['./modern.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    NgIf,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    FuseFullscreenComponent,
    RouterLink,
    UserComponent,
    RouterOutlet,
    TranslocoModule,
  ],
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation;
  pageTitle = 'My Charge';
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  logo = 'assets/images/logos/l-fleet-ezvolt.png';

  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private _authService: AuthService
  ) {
    const partnerId = _authService.partnerId?.trim();

    if (partnerId && !isEzvoltPartner(partnerId)) {
      this.logo = `assets/images/logos/l-${partnerId}.png`;
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
        this.updatePageTitle();
      });

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => this.updatePageTitle());

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  private updatePageTitle(): void {
    if (!this.navigation?.default) {
      return;
    }

    const currentPath = this.normalizePath(this._router.url);
    const activeItem = this.flattenNavigation(this.navigation.default)
      .filter((item) => !!item.title && !!item.link)
      .filter((item) => {
        const itemPath = this.normalizePath(item.link!);
        return itemPath === '/'
          ? currentPath === '/'
          : currentPath === itemPath || currentPath.startsWith(itemPath + '/');
      })
      .sort(
        (left, right) =>
          this.normalizePath(right.link!).length -
          this.normalizePath(left.link!).length
      )[0];

    this.pageTitle = activeItem?.title ?? 'My Charge';
  }

  private flattenNavigation(items: FuseNavigationItem[]): FuseNavigationItem[] {
    return items.reduce<FuseNavigationItem[]>(
      (result, item) => [
        ...result,
        item,
        ...this.flattenNavigation(item.children ?? []),
      ],
      []
    );
  }

  private normalizePath(value: string): string {
    const path = value.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');
    return path ? '/' + path : '/';
  }
}
